import "./PDFViewer.css";
import { useEffect, useMemo, useState, useRef } from "react";
import { createPluginRegistration } from "@embedpdf/core";
import { EmbedPDF } from "@embedpdf/core/react";
import { usePdfiumEngine } from "@embedpdf/engines/react";
import { RotatePluginPackage } from "@embedpdf/plugin-rotate/react";
import { ViewportPluginPackage } from "@embedpdf/plugin-viewport/react";
import { ScrollPluginPackage } from "@embedpdf/plugin-scroll/react";
import {
  DocumentContent,
  DocumentManagerPluginPackage,
} from "@embedpdf/plugin-document-manager/react";
import { RenderPluginPackage } from "@embedpdf/plugin-render/react";
import {
  ZoomPluginPackage,
  ZoomMode,
} from "@embedpdf/plugin-zoom/react";
import { InteractionManagerPluginPackage } from "@embedpdf/plugin-interaction-manager/react";
import { TilingPluginPackage } from "@embedpdf/plugin-tiling/react";
import { PanPluginPackage } from "@embedpdf/plugin-pan/react";

import NavButton from "@common-components/NavButton/NavButton";
import Spinner from "@common-components/Spinner/Spinner";
import SelectSheet from "@components/SelectSheet/SelectSheet";
import RotationControls from "@components/PDFViewer/TabletPDFVewerTool/RotationControl";
import RenderPDF from "@components/PDFViewer/TabletPDFVewerTool//RenderPDF";

function TabletPDFViewer({
  pdfPaths,
  sheetNames,
  currentPdfIndex,
  setCurrentPdfIndex,
}) {
  // state and ref to display a loading spinner while pdf is being rendered
  const viewerRef = useRef();
  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    setShowLoader(true);
  }, [currentPdfIndex]);

  const validPdfPaths = useMemo(
    () => (Array.isArray(pdfPaths) ? pdfPaths : []),
    [pdfPaths],
  );

  const plugins = useMemo(() => {
    if (!validPdfPaths[currentPdfIndex]) return [];
    return [
      createPluginRegistration(DocumentManagerPluginPackage, {
        initialDocuments: [{ url: validPdfPaths[currentPdfIndex] }],
      }),
      createPluginRegistration(ViewportPluginPackage),
      createPluginRegistration(ScrollPluginPackage),
      createPluginRegistration(RenderPluginPackage),
      createPluginRegistration(InteractionManagerPluginPackage),
      createPluginRegistration(RotatePluginPackage),
      createPluginRegistration(ZoomPluginPackage, {
        defaultZoomLevel: ZoomMode.FitPage,
        minZoom: 0.8,
        maxZoom: 3,
      }),
      createPluginRegistration(PanPluginPackage, {
        defaultMode: "mobile",
      }),
      createPluginRegistration(TilingPluginPackage, {
        tileSize: 768,
        overlapPx: 2.5,
        extraRings: 0,
      }),
    ];
  }, [currentPdfIndex]);

  const { engine, isLoading } = usePdfiumEngine();

  if (isLoading || !engine) return <div>Loading PDF Engine...</div>;

  return (
    <>
      {showLoader &&
        <div style={{
          height: "100vh",
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,255,255,0.7)",
          zIndex: 9999,
        }}>
          <Spinner />
        </div>
      }

      <div className="PDFViewer__container">
        {validPdfPaths.length > 1 && (
          <NavButton
            currentIndex={currentPdfIndex}
            setCurrentIndex={setCurrentPdfIndex}
            currentList={validPdfPaths}
          />
        )}

        <div
          style={{
            height: "100vh",
            width: "100%",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <EmbedPDF key={currentPdfIndex} engine={engine} plugins={plugins}>
            {({ activeDocumentId }) =>
              activeDocumentId && (
                <>
                  <div className="PDFViewer__SheetSelection--container">
                    <SelectSheet
                      sheetNames={sheetNames}
                      currentPdfIndex={currentPdfIndex}
                      setCurrentIndex={setCurrentPdfIndex}
                    />

                    <RotationControls documentId={activeDocumentId} />
                  </div>

                  <div ref={viewerRef} style={{ height: "calc(100vh - 80px)", width: "100%", position: "relative" }}>
                    <DocumentContent documentId={activeDocumentId}>
                      {({ isLoaded }) =>
                        isLoaded && (
                          <RenderPDF
                            activeDocumentId={activeDocumentId}
                            setShowLoader={setShowLoader}
                            viewerRef={viewerRef}
                          />
                        )
                      }
                    </DocumentContent>
                  </div>
                </>
              )
            }
          </EmbedPDF>
        </div>
      </div>
    </>
  );
}

export default TabletPDFViewer;
